import type { AssetAttachment, DownloadAttachmentFunction } from '@repo/types/attachment';
import type { LogErrorFunction } from '@repo/utils';

const MIME_TYPE_MAP: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    jfif: 'image/jpeg',
    tiff: 'image/tiff',
    gif: 'image/gif',
    webp: 'image/webp',
};

const getMimeType = (fileType: string | undefined): string => (fileType && MIME_TYPE_MAP[fileType.toLowerCase()]) || '';

export const useAttachment = (
    item: AssetAttachment,
    downloadAttachment: DownloadAttachmentFunction,
    logError: LogErrorFunction
): { downloadFile: () => Promise<void>; viewFile: () => Promise<void> } => {
    const fileReference = item.fileReference;
    const fileLocation = item.attachmentLocation;

    if (!fileReference) {
        logError(new Error('File reference is missing'));
        throw new Error('File is missing');
    }

    const downloadFile = async () => {
        try {
            const response = await downloadAttachment(fileReference, fileLocation);
            const blob = new Blob([response], { type: getMimeType(item.fileType) });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = item.originalFileName ?? '';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            logError(error);
        }
    };

    const viewFile = async () => {
        const newWindow = window.open('', '_blank');

        try {
            const response = await downloadAttachment(fileReference, fileLocation);
            const blob = new Blob([response], { type: getMimeType(item.fileType) });
            const url = URL.createObjectURL(blob);

            if (!newWindow || newWindow.closed) {
                URL.revokeObjectURL(url);
                return;
            }

            newWindow.location.href = url;

            newWindow.addEventListener('load', () => {
                URL.revokeObjectURL(url);
            });
        } catch (error) {
            newWindow?.close();
            logError(error);
        }
    };

    return { downloadFile, viewFile };
};
